// --- Dependencies -------------------------------------------------------------------------------
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useTranslation from "@/hooks/useTranslation";
import "./styles.css";

// --- Constants ----------------------------------------------------------------------------------

/** Real-looking names rather than `yourslug`, so the example reads as a menu someone could open. */
const EXAMPLE_STORE_SLUG = "rofee";
const EXAMPLE_BRANCH_SLUG = "maadi";

// --- Types --------------------------------------------------------------------------------------

export type SlugInfoPopupDetails = {
    /** A store slug is the whole subdomain; a branch slug is a query on top of it. */
    variant?: "store" | "branch";
};

type Props = {
    onClose: () => void;
    details?: SlugInfoPopupDetails;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * "Slug" is jargon a cafe owner has no reason to know, and the value is effectively permanent once
 * QR codes are on the tables. This shows the word in a real URL with the part they choose picked out,
 * says what it costs to change, then lists the rules — in that order, because the example teaches
 * faster than the rules do. Both slugs obey the same rules, so they share one popup.
 */
export default function SlugInfoPopup({ onClose, details }: Props) {
    const t = useTranslation();

    const isBranch = details?.variant === "branch";
    const storeHost = `${EXAMPLE_STORE_SLUG}.${constants.menuDomain}`;

    const title = isBranch ? t.slugInfo.branchTitle : t.slugInfo.storeTitle;
    const definition = isBranch ? t.slugInfo.branchDefinition : t.slugInfo.storeDefinition;
    const qrNote = isBranch ? t.slugInfo.branchQrNote : t.slugInfo.storeQrNote;

    const urlPrefix = isBranch ? `${storeHost}?branch=` : "";
    const urlSlug = isBranch ? EXAMPLE_BRANCH_SLUG : EXAMPLE_STORE_SLUG;
    const urlSuffix = isBranch ? "" : `.${constants.menuDomain}`;

    return (
        <div className="slug-info">
            <div className="slug-info-head">
                <div className="slug-info-head-icon">
                    <img src="/icons/info-circle-icon.svg" alt="" width={20} height={20} />
                </div>
                <div className="slug-info-head-texts">
                    <p className="slug-info-title">{title}</p>
                    <p className="slug-info-definition">{definition}</p>
                </div>
            </div>

            <div className="slug-info-example">
                <span className="slug-info-example-label">{t.slugInfo.exampleLabel}</span>

                {/* Always latin-ordered: an RTL page would otherwise scatter the parts of the URL. */}
                <p className="slug-info-url" dir="ltr">
                    {urlPrefix && <span className="slug-info-url-rest">{urlPrefix}</span>}
                    <span className="slug-info-url-slug">{urlSlug}</span>
                    {urlSuffix && <span className="slug-info-url-rest">{urlSuffix}</span>}
                </p>

                <div className="slug-info-pointer" dir="ltr">
                    <span className="slug-info-pointer-mark" />
                    <span className="slug-info-pointer-text">{t.slugInfo.exampleCaption}</span>
                </div>
            </div>

            <div className="slug-info-qr">
                <img src="/icons/qr-code-icon.svg" alt="" width={17} height={17} />
                <p>{qrNote}</p>
            </div>

            <div className="slug-info-rules">
                <span className="slug-info-rules-title">{t.slugInfo.rulesTitle}</span>
                <ul className="slug-info-rules-list">
                    {t.slugInfo.rules.map((rule) => (
                        <li key={rule} className="slug-info-rule">
                            <span className="slug-info-rule-tick">
                                <img src="/icons/check-icon.svg" alt="" width={12} height={12} />
                            </span>
                            <span className="slug-info-rule-text">{rule}</span>
                        </li>
                    ))}
                </ul>
            </div>

            <button type="button" className="slug-info-button" onClick={onClose}>
                {t.slugInfo.button}
            </button>
        </div>
    );
}
